import { MecordColors } from "Constants/Color";
import React from "react";
import { Spinner } from "react-bootstrap";

interface PrimarySubmitButtonProps {
  label?: string; // Button label text (defaults to "Submit")
  icon?: string; // Optional icon class
  type?: "button" | "submit" | "reset"; // Button type (defaults to "submit")
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; // Optional click handler
  className?: string; // Additional CSS classes
  disabled?: boolean; // Disabled state
  isLoading?: boolean; // Loading state
  loadingLabel?: string; // Loading text (defaults to "Submitting...")
  style?: React.CSSProperties; // Additional inline styles
  variantColor?: string; // Override default theme color (e.g. for Approve=green, Reject=red)
}

const PrimarySubmitButton: React.FC<PrimarySubmitButtonProps> = ({
  label = "Submit",
  icon,
  type = "submit",
  onClick,
  className = "",
  disabled = false,
  isLoading = false,
  loadingLabel = "Submitting...",
  style,
  variantColor,
}) => {
  const themeColor = MecordColors?.ThemeBGColor;
  const effectiveColor = variantColor ?? style?.backgroundColor ?? style?.borderColor ?? themeColor;
  const color = typeof effectiveColor === "string" ? effectiveColor : themeColor;
  const colorClass = `custom-primary-submit-btn-${String(color).replace(/[^a-zA-Z0-9]/g, "")}`;

  const baseClasses = `btn custom-primary-submit-button ${colorClass} ${className}`.trim();
  const iconClasses = icon ? `${icon} align-baseline me-1` : "";
  const combinedClasses = icon ? `hstack gap-2 ${baseClasses}`.trim() : baseClasses;

  const cssVarStyle: React.CSSProperties = {
    ...(style?.fontSize
      ? ({ ["--psb-font-size" as any]: String(style.fontSize) } as React.CSSProperties)
      : {}),
    ...(style?.padding
      ? ({ ["--psb-padding" as any]: String(style.padding) } as React.CSSProperties)
      : {}),
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: color,
    borderColor: color,
    color: "#fff",
    borderRadius: "4px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: disabled || isLoading ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    opacity: disabled || isLoading ? 0.6 : 1,
    border: `1px solid ${color}`,
    ...cssVarStyle,
    ...style,
  };

  const buttonContent = (
    <>
      {isLoading ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            style={{ width: '14px', height: '14px', borderWidth: '2px' }}
          />
          {loadingLabel}
        </>
      ) : (
        <>
          {icon && <i className={iconClasses}></i>}
          {label}
        </>
      )}
    </>
  );

  return (
    <>
      <style>
        {`
          .custom-primary-submit-button.${colorClass} {
            background-color: ${color} !important;
            border-color: ${color} !important;
            color: #fff !important;
            border-radius: 4px !important;
            padding: var(--psb-padding, 8px 16px) !important;
            font-size: var(--psb-font-size, 14px) !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            border: 1px solid ${color} !important;
          }
          .custom-primary-submit-button.${colorClass}:hover:not(:disabled) {
            background-color: ${color} !important;
            border-color: ${color} !important;
            opacity: 0.9;
          }
          .custom-primary-submit-button.${colorClass}:focus {
            background-color: ${color} !important;
            border-color: ${color} !important;
            box-shadow: 0 0 0 0.2rem ${color}40 !important;
          }
          .custom-primary-submit-button.${colorClass}:disabled {
            background-color: ${color} !important;
            border-color: ${color} !important;
            opacity: 0.6;
            cursor: not-allowed !important;
          }
        `}
      </style>
      <button
        type={type}
        className={combinedClasses}
        disabled={disabled || isLoading}
        style={buttonStyle}
        onClick={onClick}
      >
        {buttonContent}
      </button>
    </>
  );
};

export default PrimarySubmitButton;

